/**
 * Centralized logging service for the application
 * Provides different log levels and can be configured for production
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  data?: unknown;
  stack?: string;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;
  private isProduction: boolean;

  private constructor() {
    this.isProduction = import.meta.env.PROD;
    this.logLevel = this.isProduction ? LogLevel.WARN : LogLevel.DEBUG;
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private sanitizeData(data: unknown): unknown {
    if (!data) return data;

    // Deep clone to avoid modifying original
    const sanitized = JSON.parse(JSON.stringify(data)) as Record<
      string,
      unknown
    >;

    // Remove sensitive fields
    const sensitiveFields = [
      'password',
      'token',
      'refresh_token',
      'authorization',
      'secret',
      'key'
    ];

    const sanitizeObject = (obj: unknown): unknown => {
      if (typeof obj !== 'object' || obj === null) return obj;

      if (Array.isArray(obj)) {
        return obj.map(sanitizeObject);
      }

      const result = { ...obj } as Record<string, unknown>;
      for (const key in result) {
        if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
          result[key] = '[REDACTED]';
        } else if (typeof result[key] === 'object') {
          result[key] = sanitizeObject(result[key]);
        }
      }
      return result;
    };

    return sanitizeObject(sanitized);
  }

  private log(
    level: LogLevel,
    message: string,
    data?: unknown,
    error?: Error
  ): void {
    if (level < this.logLevel) return;

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      data: this.sanitizeData(data),
      stack: error?.stack
    };

    // In production, only log warnings and errors
    if (this.isProduction && level >= LogLevel.WARN) {
      // In a real app, you would send this to a logging service
      // For now, we'll just use console for errors
      if (level === LogLevel.ERROR) {
        console.error(
          `[${entry.timestamp.toISOString()}] ERROR: ${message}`,
          entry.data
        );
      } else if (level === LogLevel.WARN) {
        console.warn(
          `[${entry.timestamp.toISOString()}] WARN: ${message}`,
          entry.data
        );
      }
    } else if (!this.isProduction) {
      // Development logging
      const levelNames = ['DEBUG', 'INFO', 'WARN', 'ERROR'];
      const levelName = levelNames[level];
      const color =
        level === LogLevel.ERROR
          ? 'color: red'
          : level === LogLevel.WARN
            ? 'color: orange'
            : level === LogLevel.INFO
              ? 'color: blue'
              : 'color: gray';

      console.log(
        `%c[${entry.timestamp.toISOString()}] ${levelName}: ${message}`,
        color,
        entry.data || ''
      );
    }
  }

  public debug(message: string, data?: unknown): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  public info(message: string, data?: unknown): void {
    this.log(LogLevel.INFO, message, data);
  }

  public warn(message: string, data?: unknown): void {
    this.log(LogLevel.WARN, message, data);
  }

  public error(message: string, data?: unknown, error?: Error): void {
    this.log(LogLevel.ERROR, message, data, error);
  }
}

export const logger = Logger.getInstance();
